import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import axios from 'axios';
import ConfirmationModal from '../components/ConfirmationModal';

const AdminContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const AdminHeader = styled.div`
  position: relative;
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: white;
  padding: 3rem 2rem;
  border-radius: 16px;
  margin: 0 0 3rem 0;

  @media (max-width: 767px) {
    margin: 0 -2rem 3rem -2rem;
    border-radius: 0;
  }
`;

const AdminTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const LoginForm = styled.form`
  max-width: 400px;
  margin: 2rem auto;
  padding: 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
`;

const LoginInput = styled.input`
  width: 100%;
  padding: 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  margin-bottom: 1rem;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
  }
`;

const LoginButton = styled.button`
  width: 100%;
  padding: 1rem;
  background-color: #4299e1;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  
  &:hover {
    background-color: #3182ce;
  }
  
  &:disabled {
    background-color: #a0aec0;
    cursor: not-allowed;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: #4299e1;
`;

const StatLabel = styled.div`
  color: #718096;
  margin-top: 0.5rem;
`;

const MessagesList = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  overflow: hidden;
`;

const MessagesHeader = styled.div`
  padding: 1.5rem;
  background: #f7fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const MessagesTitle = styled.h2`
  margin: 0;
  color: #2d3748;
`;

const BulkActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const LogoutButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.5rem 1rem;
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;

  &:hover {
    background-color: rgba(255, 255, 255, 0.3);
  }
`;

const NavigationSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
`;

const NavCard = styled(Link)`
  display: block;
  background: white;
  border-radius: 12px;
  padding: 2rem;
  text-decoration: none;
  color: inherit;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  border: 1px solid #e2e8f0;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.15);
  }
`;

const NavCardIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 1rem;
`;

const NavCardTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
  color: #2d3748;
`;

const NavCardDescription = styled.p`
  color: #718096;
  font-size: 0.9rem;
  line-height: 1.4;
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  
  &.approve {
    background-color: #48bb78;
    color: white;
    
    &:hover {
      background-color: #38a169;
    }
  }
  
  &.reject {
    background-color: #f56565;
    color: white;
    
    &:hover {
      background-color: #e53e3e;
    }
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MessageItem = styled(motion.div)`
  border-bottom: 1px solid #e2e8f0;
  padding: 1.5rem;
  
  &:last-child {
    border-bottom: none;
  }
  
  &.unapproved {
    background-color: #fff5f5;
  }
  
  &.approved {
    background-color: #f0fff4;
  }
`;

const MessageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const MessageInfo = styled.div`
  flex: 1;
`;

const MessageAuthor = styled.div`
  font-weight: bold;
  color: #2d3748;
`;

const MessageEmail = styled.div`
  color: #718096;
  font-size: 0.9rem;
`;

const MessageDate = styled.div`
  color: #718096;
  font-size: 0.9rem;
`;

const MessageMeta = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const MessageStatus = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: bold;
  
  &.approved {
    background-color: #c6f6d5;
    color: #22543d;
  }
  
  &.pending {
    background-color: #fed7d7;
    color: #742a2a;
  }
`;

const MessageText = styled.div`
  color: #4a5568;
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const MessageActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-right: 1rem;
`;

const ErrorMessage = styled.div`
  background-color: #fed7d7;
  color: #742a2a;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
`;

const LoadingSpinner = styled.div`
  text-align: center;
  color: #718096;
  padding: 2rem;
`;

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMessages, setSelectedMessages] = useState(new Set());
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      const authToken = token || localStorage.getItem('authToken');
      const response = await axios.get('/api/admin/messages', {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      setMessages(response.data.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
    } catch (error) {
      setError('Failed to fetch messages');
      if (error.response?.status === 401) {
        setIsAuthenticated(false);
        localStorage.removeItem('authToken');
        setToken('');
      }
    } finally {
      setLoading(false);
    }
  }, [token, setMessages, setLoading, setError, setIsAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchMessages();
    }
  }, [isAuthenticated, fetchMessages]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Login with password to get JWT token
      const response = await axios.post('/api/admin/login', {
        password: password
      });

      const { token: authToken } = response.data;
      setToken(authToken);
      setIsAuthenticated(true);
      localStorage.setItem('authToken', authToken);
    } catch (error) {
      if (error.response?.status === 401) {
        setError('Invalid password');
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const approveMessage = async (messageId) => {
    try {
      const authToken = token || localStorage.getItem('authToken');
      await axios.post(`/api/admin/messages/${messageId}/approve`, {}, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      fetchMessages();
    } catch (error) {
      setError('Failed to approve message');
      if (error.response?.status === 401) {
        setIsAuthenticated(false);
        localStorage.removeItem('authToken');
        setToken('');
      }
    }
  };

  const handleDeleteClick = (messageId) => {
    setMessageToDelete(messageId);
    setShowConfirmModal(true);
  };

  const deleteMessage = async () => {
    if (!messageToDelete) return;

    try {
      const authToken = token || localStorage.getItem('authToken');
      await axios.delete(`/api/admin/messages/${messageToDelete}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      fetchMessages();
      setMessageToDelete(null);
    } catch (error) {
      setError('Failed to delete message');
      if (error.response?.status === 401) {
        setIsAuthenticated(false);
        localStorage.removeItem('authToken');
        setToken('');
      }
    }
  };

  const bulkApprove = async () => {
    if (selectedMessages.size === 0) return;

    try {
      const authToken = token || localStorage.getItem('authToken');
      await axios.post('/api/admin/messages/bulk-approve', {
        messageIds: Array.from(selectedMessages)
      }, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      setSelectedMessages(new Set());
      fetchMessages();
    } catch (error) {
      setError('Failed to bulk approve messages');
      if (error.response?.status === 401) {
        setIsAuthenticated(false);
        localStorage.removeItem('authToken');
        setToken('');
      }
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setToken('');
    setPassword('');
    localStorage.removeItem('authToken');
    setMessages([]);
    setSelectedMessages(new Set());
    setError(null);
  };

  const toggleMessageSelection = (messageId) => {
    const newSelection = new Set(selectedMessages);
    if (newSelection.has(messageId)) {
      newSelection.delete(messageId);
    } else {
      newSelection.add(messageId);
    }
    setSelectedMessages(newSelection);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Check for stored JWT token on component mount
  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    if (storedToken) {
      setToken(storedToken);
      setIsAuthenticated(true);
    }
  }, []);

  if (!isAuthenticated) {
    return (
      <AdminContainer>
        <AdminHeader>
          <AdminTitle>Admin Dashboard</AdminTitle>
        </AdminHeader>
        
        <LoginForm onSubmit={handleLogin}>
          <h3>Admin Login</h3>
          <LoginInput
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <LoginButton type="submit" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </LoginButton>
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </LoginForm>
      </AdminContainer>
    );
  }

  const approvedMessages = messages.filter(msg => msg.approved);
  const pendingMessages = messages.filter(msg => !msg.approved);

  return (
    <AdminContainer>
      <AdminHeader>
        <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
        <AdminTitle>Message Administration</AdminTitle>
      </AdminHeader>

      <NavigationSection>
        <NavCard to="/admin/analytics">
          <NavCardIcon>📊</NavCardIcon>
          <NavCardTitle>Analytics Dashboard</NavCardTitle>
          <NavCardDescription>
            View Google Analytics, Search Console data, and Core Web Vitals performance metrics
          </NavCardDescription>
        </NavCard>
      </NavigationSection>

      <StatsGrid>
        <StatCard>
          <StatNumber>{messages.length}</StatNumber>
          <StatLabel>Total Messages</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{pendingMessages.length}</StatNumber>
          <StatLabel>Pending Approval</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{approvedMessages.length}</StatNumber>
          <StatLabel>Approved</StatLabel>
        </StatCard>
      </StatsGrid>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <MessagesList>
        <MessagesHeader>
          <MessagesTitle>Messages</MessagesTitle>
          <BulkActions>
            {selectedMessages.size > 0 && (
              <ActionButton className="approve" onClick={bulkApprove}>
                Approve Selected ({selectedMessages.size})
              </ActionButton>
            )}
            <ActionButton onClick={fetchMessages}>
              Refresh
            </ActionButton>
          </BulkActions>
        </MessagesHeader>

        {loading ? (
          <LoadingSpinner>Loading messages...</LoadingSpinner>
        ) : (
          messages.map((message) => (
            <MessageItem
              key={message.id}
              className={message.approved ? 'approved' : 'unapproved'}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <MessageHeader>
                <MessageInfo>
                  <MessageAuthor>{message.name}</MessageAuthor>
                  {message.email && <MessageEmail>{message.email}</MessageEmail>}
                  <MessageDate>{formatDate(message.timestamp)}</MessageDate>
                </MessageInfo>
                <MessageMeta>
                  <MessageStatus className={message.approved ? 'approved' : 'pending'}>
                    {message.approved ? 'Approved' : 'Pending'}
                  </MessageStatus>
                  <CheckboxContainer>
                    <input
                      type="checkbox"
                      checked={selectedMessages.has(message.id)}
                      onChange={() => toggleMessageSelection(message.id)}
                    />
                  </CheckboxContainer>
                </MessageMeta>
              </MessageHeader>
              
              <MessageText>{message.message}</MessageText>
              
              <MessageActions>
                {!message.approved && (
                  <ActionButton
                    className="approve"
                    onClick={() => approveMessage(message.id)}
                  >
                    Approve
                  </ActionButton>
                )}
                <ActionButton
                  className="reject"
                  onClick={() => handleDeleteClick(message.id)}
                >
                  Delete
                </ActionButton>
              </MessageActions>
            </MessageItem>
          ))
        )}
      </MessagesList>

      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setMessageToDelete(null);
        }}
        onConfirm={deleteMessage}
        title="Delete Message"
        message="Are you sure you want to delete this message? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </AdminContainer>
  );
};

export default AdminDashboard;
